import { useValuemappingRedirects } from '@Hooks';
import {
	ActionBar,
	ActionBarItemGroup,
	Button,
	ContentCard,
	DropdownSelect,
	EmptyTableBody,
	rem,
	SensorIllustration,
	StylovyzeSearchBar,
	StylovyzeTableBody,
	StylovyzeTableFooter,
	StylovyzeTableHeaderProps,
	useSettings,
} from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations';
import { TFunc } from '@Translations/types';
import {
	PaginationInfo,
	ROWS_PER_PAGE,
	ValuemappingManagerStoreState,
	ValueMappingProfileSimplified,
	ValueMappingProfilesTableState,
} from '@Types';
import { VALUEMAPPING_ROUTES } from '@Types/routes';
import { addRouteNamespace } from '@Utils';
import { momentToLuxonFormat } from '@Utils/formatDateTime';
import { DateTime } from 'luxon';
import React from 'react';

const headers = (t: TFunc): StylovyzeTableHeaderProps[] => [
	{
		key: 'name',
		cell: t('Profile Name'),
		width: '20%',
		align: 'left',
		sortable: true,
	},
	{
		key: 'sensors',
		cell: t('Sensors'),
		width: '65%',
		sortable: true,
	},
	{
		key: 'valueMappingProfileModifiedAt',
		cell: t('Last Modified'),
		width: '23%',
		sortable: true,
	},
];

interface ValueMappingsTableProps {
	initialized: boolean;
	list: ValueMappingProfileSimplified[];
	tableState: ValueMappingProfilesTableState;
	pagination: PaginationInfo;
	filters: ValuemappingManagerStoreState['valueMappingProfiles']['filters'];
	fetchValueMappingProfiles: (page: number) => void;
	onChangeValueMappingProfilesPageSize: (
		size: number,
		pageSizeOptionsIndex: number,
	) => void;
	onChangeSort: (
		event: React.MouseEvent<unknown>,
		name: string | number,
		order: 'asc' | 'desc',
	) => void;
	onChangeSearch: (query: string) => void;
	filterChangedHandler: (name: string, value: string) => void;
}

const ValueMappingsTable = (props: ValueMappingsTableProps) => {
	const {
		initialized,
		list,
		tableState,
		pagination,
		filters,
		fetchValueMappingProfiles,
		onChangeValueMappingProfilesPageSize,
		onChangeSort,
		onChangeSearch,
		filterChangedHandler,
	} = props;
	const { t } = useGlobalization();
	const { redirectToCreatePage } = useValuemappingRedirects();
	const { companySettings } = useSettings();

	const onChangePageHandler = (_event: unknown, page: number) => {
		fetchValueMappingProfiles(page);
	};

	const rows = list.map((el, idx) => {
		return {
			key: `valuemapping-row-${idx}`,
			cells: [
				el.name,
				el.sensors,
				el.valueMappingProfileModifiedAt
					? DateTime.fromSQL(
							el.valueMappingProfileModifiedAt,
					  ).toFormat(
							momentToLuxonFormat(companySettings.dateFormat),
					  )
					: '',
			],
			pathToGoWhenClicked: addRouteNamespace(
				VALUEMAPPING_ROUTES.edit.replace(/:profileId/g, el.id),
			),
		};
	});

	const rowsPerPageOptions = ROWS_PER_PAGE.map((value, index) => ({
		name: value.toString(),
		onClick: () => onChangeValueMappingProfilesPageSize(value, index),
	}));

	const header = React.useMemo(() => {
		const onChangeFilterHandler =
			(name: string) => (event: React.ChangeEvent<{ value: unknown }>) =>
				filterChangedHandler(name, event.target.value as string);

		const sensorTypeOptions =
			filters.sensorType.options?.map(option => {
				const optionValue = {
					key: option,
					value: option,
					label: option,
				};
				if (option === 'All sensor types') {
					optionValue.label = t('All sensor types');
				}
				return optionValue;
			}) || [];
		return (
			<ActionBar>
				<ActionBarItemGroup columns={2}>
					<StylovyzeSearchBar
						placeholder={t('Search a value mapping')}
						value={tableState.searchQuery}
						searchInProgress={!initialized}
						style={{ width: '300px' }}
						handleSearchSubmit={(query: string) => {
							onChangeSearch(query);
						}}
						textFieldProps={{
							fullWidth: true,
						}}
						dataCy="valueMappingsSearch"
					/>
					<DropdownSelect
						dataCy="sensorTypeFilter"
						currentSelection={filters.sensorType.selected}
						title={t('Sensor Type')}
						options={sensorTypeOptions}
						onChange={onChangeFilterHandler('sensorType')}
						inlineStyle={{ width: '130px' }}
					/>
				</ActionBarItemGroup>
				<ActionBarItemGroup columns={1}>
					<Button
						data-cy="createNewValueMapping"
						onClick={redirectToCreatePage}
						color="primary"
						variant="contained">
						{t('Create Value Mapping')}
					</Button>
				</ActionBarItemGroup>
			</ActionBar>
		);
	}, [
		filters.sensorType.options,
		filters.sensorType.selected,
		filterChangedHandler,
		initialized,
		onChangeSearch,
	]);

	const footer = React.useMemo(
		() => (
			<StylovyzeTableFooter
				pagination={{
					onChange: onChangePageHandler,
					currentPage: pagination.current,
					totalNumberOfPages: pagination.pages,
					dataCy: 'valueMappingsPagination',
				}}
			/>
		),
		[onChangePageHandler, pagination],
	);

	return (
		<ContentCard header={header} footer={footer} applyBodyPadding={false}>
			<StylovyzeTableBody
				initialized={initialized}
				dataCy="valueMappingsTable"
				headers={headers(t)}
				rows={rows}
				minHeight={rem(600)}
				emptyTableBody={
					<EmptyTableBody
						dataCy="value-mapping-empty-table-body"
						title={t('There are no value mappings to display.')}
						subtitle={t(
							'Value mapping creates rules to automatically update particular values in the sensor data.',
						)}
						icon={<SensorIllustration />}
						color={'#4A6067'}
						CTA={
							<Button
								variant="contained"
								color="primary"
								style={{ marginRight: 8 }}
								onClick={redirectToCreatePage}>
								{t('Create Value Mapping')}
							</Button>
						}
					/>
				}
				useSkeleton={{ rowCount: ROWS_PER_PAGE[tableState.limitIndex] }}
				sort={{
					name: tableState.sort.field,
					order: tableState.sort.sortOrder,
					onRequestSort: onChangeSort,
				}}
				rowsPerPage={{
					selectedIndex: tableState.limitIndex,
					options: rowsPerPageOptions,
				}}
			/>
		</ContentCard>
	);
};

export default ValueMappingsTable;
